/**
 * HomepageDynamicMemories ✦ iter234 — auto-rotating hero strip of
 * the 5 highest-inspiration-score public events from the last 12
 * months. Lives on the homepage between the editorial layers and the
 * venue map, so visitors land directly on proof — every new
 * completed booking quietly earns a spot on the front door.
 *
 * Rotates every 6 seconds (pauses on hover, respects
 * prefers-reduced-motion). Dot pagination + arrow nav for manual
 * stepping. Empty state hides the whole section so the page stays
 * calm on fresh deploys.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.72)';
const GOLD = '#D4AF5E';
const GOLD_MUTED = 'rgba(212,170,82,0.78)';
const WALNUT_DEEP = '#080503';
const WALNUT = '#0E0805';

const ROTATE_MS = 6000;

const mediaUrl = (path) =>
  path?.startsWith('/api/')
    ? `${process.env.REACT_APP_BACKEND_URL}${path}`
    : path;

const formatDate = (iso) => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso.slice(0, 10);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1]} ${m[1]}`;
};

const HomepageDynamicMemories = () => {
  const [events, setEvents] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/public-events/top-memories?months=12&limit=5`,
        );
        if (cancelled) return;
        setEvents(r.data?.events || []);
      } catch (e) {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-rotation — respects prefers-reduced-motion and pauses on hover
  useEffect(() => {
    if (events.length <= 1 || paused) return;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    timerRef.current = setTimeout(
      () => setIndex(i => (i + 1) % events.length),
      ROTATE_MS,
    );
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [index, events.length, paused]);

  const go = useCallback((dir) => {
    setIndex(i => {
      const n = events.length || 1;
      return ((i + dir) % n + n) % n;
    });
  }, [events.length]);

  if (!events.length) return null;

  const active = events[index] || events[0];
  const cover = mediaUrl(active.cover_image)
    || 'https://customer-assets.emergentagent.com/job_dcc1c8ae-c90a-45ec-9858-0c59a5447018/artifacts/5ctbscdn_1000039300.png';

  return (
    <section
      data-testid="homepage-dynamic-memories"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        background: `linear-gradient(180deg, ${WALNUT} 0%, ${WALNUT_DEEP} 100%)`,
        padding: 'clamp(64px, 9vw, 120px) clamp(20px, 5vw, 64px)',
        borderTop: `1px solid rgba(212,170,82,0.10)`,
        position: 'relative', overflow: 'hidden',
      }}>
      <div style={{ maxWidth: 1240, margin: '0 auto', position: 'relative' }}>
        <header style={{ textAlign: 'center', marginBottom: 'clamp(36px, 5vw, 60px)' }}>
          <div style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
            color: GOLD_MUTED, marginBottom: 16,
          }}>✦ Recently captured</div>
          <h2 style={{
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(30px, 4.6vw, 50px)',
            color: IVORY, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.08,
          }}>The memories we&apos;ve made this year</h2>
          <p style={{
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 'clamp(13px, 1.4vw, 16px)', color: IVORY_DIM,
            maxWidth: 580, margin: '18px auto 0', lineHeight: 1.65,
          }}>
            Real evenings, real couples, real keepsakes. Every event below
            became someone&apos;s favourite night.
          </p>
        </header>

        <div data-testid="dynamic-memories-stage" style={{
          position: 'relative',
          borderRadius: 18, overflow: 'hidden',
          boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
          border: '1px solid rgba(212,170,82,0.16)',
          minHeight: 'clamp(380px, 56vw, 520px)',
        }}>
          {/* Cross-fading slides — only active gets opacity 1 */}
          {events.map((ev, i) => (
            <Slide
              key={ev.slug}
              ev={ev}
              isActive={i === index}
              cover={mediaUrl(ev.cover_image) || cover}
            />
          ))}

          {/* Prev / Next nav */}
          {events.length > 1 && (
            <>
              <NavArrow
                direction="prev" onClick={() => go(-1)}
                testid="dynamic-memories-prev" />
              <NavArrow
                direction="next" onClick={() => go(1)}
                testid="dynamic-memories-next" />
            </>
          )}
        </div>

        {/* Dot pagination */}
        {events.length > 1 && (
          <div data-testid="dynamic-memories-dots" style={{
            display: 'flex', justifyContent: 'center', gap: 10,
            marginTop: 24,
          }}>
            {events.map((ev, i) => (
              <button
                key={ev.slug}
                type="button"
                onClick={() => setIndex(i)}
                data-testid={`dynamic-memories-dot-${i}`}
                aria-label={`Show ${ev.title}`}
                aria-current={i === index}
                style={{
                  width: i === index ? 28 : 10, height: 10,
                  borderRadius: 999,
                  background: i === index ? GOLD : 'rgba(212,170,82,0.28)',
                  border: 'none', cursor: 'pointer',
                  transition: 'width 0.32s ease, background 0.32s ease',
                  padding: 0,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const Slide = ({ ev, isActive, cover }) => {
  const dateLabel = formatDate(ev.event_date);
  const meta = [dateLabel, ev.county, ev.event_type, ev.package_label || ev.package_slug]
    .filter(Boolean).join(' · ');
  return (
    <article
      data-testid={`dynamic-memories-slide-${ev.slug}`}
      aria-hidden={!isActive}
      style={{
        position: 'absolute', inset: 0,
        opacity: isActive ? 1 : 0,
        pointerEvents: isActive ? 'auto' : 'none',
        transition: 'opacity 0.9s ease',
        backgroundImage: cover
          ? `linear-gradient(180deg, rgba(8,5,3,0.30) 0%, rgba(8,5,3,0.78) 60%, rgba(8,5,3,0.95) 100%), url(${cover})`
          : `linear-gradient(135deg, ${WALNUT}, ${WALNUT_DEEP})`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        display: 'flex', alignItems: 'flex-end',
      }}>
      <div style={{
        padding: 'clamp(28px, 5vw, 60px)',
        maxWidth: 720,
        fontFamily: SERIF, color: IVORY,
      }}>
        {ev.is_featured && (
          <span style={{
            display: 'inline-block',
            background: GOLD, color: WALNUT_DEEP,
            fontFamily: SERIF, fontSize: 10.5,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            padding: '4px 11px', borderRadius: 999, marginBottom: 14,
          }}>★ Featured memory</span>
        )}
        <h3 style={{
          margin: 0, fontWeight: 500,
          fontSize: 'clamp(24px, 3.6vw, 38px)', lineHeight: 1.12,
          letterSpacing: '-0.018em', color: IVORY,
        }}>{ev.venue_name || ev.title}</h3>
        {meta && (
          <div style={{
            fontStyle: 'italic', fontSize: 13.5, color: IVORY_DIM,
            marginTop: 10, letterSpacing: '0.04em',
          }}>{meta}</div>
        )}
        {ev.rating > 0 && (
          <div style={{
            color: GOLD, fontSize: 16, letterSpacing: 2, marginTop: 12,
          }}>{'★'.repeat(Math.round(ev.rating))}</div>
        )}
        {ev.review_snippet && (
          <p style={{
            fontStyle: 'italic', fontSize: 'clamp(15px, 1.55vw, 18px)',
            lineHeight: 1.6, margin: '18px 0 22px', color: IVORY,
            maxWidth: 560,
          }}>
            &ldquo;{ev.review_snippet}
            {ev.review_snippet.length >= 220 ? '…' : ''}&rdquo;
          </p>
        )}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap',
                       alignItems: 'center', marginTop: 8 }}>
          <Link
            to={`/events/${ev.slug}`}
            data-testid={`dynamic-memories-cta-${ev.slug}`}
            style={{
              background: GOLD, color: WALNUT_DEEP,
              borderRadius: 999, padding: '12px 26px',
              fontFamily: SERIF, fontSize: 13,
              letterSpacing: '0.16em', textTransform: 'uppercase',
              textDecoration: 'none', display: 'inline-block',
              boxShadow: '0 12px 32px rgba(212,170,82,0.32)',
              transition: 'transform 0.2s ease',
            }}>Read the story ✦</Link>
          {ev.venue_slug && (
            <Link to={`/venues/${ev.venue_slug}`} style={{
              color: IVORY, fontSize: 13.5, fontStyle: 'italic',
              textDecoration: 'underline', textUnderlineOffset: 4,
              textDecorationColor: 'rgba(212,170,82,0.45)',
            }}>More at {ev.venue_name || 'this venue'} →</Link>
          )}
        </div>
      </div>
    </article>
  );
};

const NavArrow = ({ direction, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    aria-label={direction === 'prev' ? 'Previous memory' : 'Next memory'}
    style={{
      position: 'absolute', top: '50%', transform: 'translateY(-50%)',
      [direction === 'prev' ? 'left' : 'right']: 18,
      width: 46, height: 46, borderRadius: '50%',
      background: 'rgba(8,5,3,0.55)', color: IVORY,
      border: '1px solid rgba(212,170,82,0.36)',
      cursor: 'pointer', fontFamily: SERIF, fontSize: 22,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
      transition: 'background 0.2s ease, transform 0.2s ease',
      zIndex: 2,
    }}>{direction === 'prev' ? '‹' : '›'}</button>
);

export default HomepageDynamicMemories;
